import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { Upload, FileText, ArrowRight, X, Sparkle, Warning, CheckCircle, Plus } from '@phosphor-icons/react'
import api from '../services/api'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog'

const ACCEPTED_EXT = ['.docx', '.md', '.markdown', '.txt']
const MAX_PASTE_CHARS = 60000
const MIN_PASTE_CHARS = 80

const PLATFORMS = ['YouTube', 'YouTube Shorts', 'LinkedIn', 'TikTok', 'Instagram']

/**
 * F3 Draft Resurrection — modal de dos pasos:
 *  1) Upload (.docx, .md, .txt) o paste → llamada a /scripts/diagnose-draft (gratis).
 *  2) Pantalla de diagnóstico con strong/weak/missing → confirmación cobra créditos
 *     y llama a /scripts/import-draft. Redirect al editor con location.state para banner.
 */
export default function DraftImportModal({ open, onClose, onImported }) {
  const navigate = useNavigate()

  const [step, setStep] = useState('upload') // upload | diagnosing | review | resurrecting
  const [mode, setMode] = useState('file') // file | paste
  const [file, setFile] = useState(null)
  const [pastedText, setPastedText] = useState('')
  const [platform, setPlatform] = useState('YouTube')
  const [durationMin, setDurationMin] = useState(8)
  const [diagnosisResult, setDiagnosisResult] = useState(null) // { diagnosis_id, diagnosis, credit_cost, ... }
  const [dragActive, setDragActive] = useState(false)
  const inputRef = useRef(null)

  useEffect(() => {
    if (!open) {
      // reset on close
      setStep('upload')
      setMode('file')
      setFile(null)
      setPastedText('')
      setPlatform('YouTube')
      setDurationMin(8)
      setDiagnosisResult(null)
      setDragActive(false)
    }
  }, [open])

  // ── upload handlers ─────────────────────────────────────────────────────

  const validateFile = (f) => {
    if (!f) return 'No se ha seleccionado ningún archivo'
    const lower = f.name.toLowerCase()
    if (!ACCEPTED_EXT.some((ext) => lower.endsWith(ext))) {
      return `Formato no soportado. Usa ${ACCEPTED_EXT.join(', ')} o pega el texto.`
    }
    if (f.size > 5 * 1024 * 1024) return 'El archivo excede los 5 MB'
    return null
  }

  const handleFile = (f) => {
    const err = validateFile(f)
    if (err) {
      toast.error(err)
      return
    }
    setFile(f)
  }

  const onDrop = (e) => {
    e.preventDefault()
    setDragActive(false)
    const f = e.dataTransfer.files?.[0]
    if (f) handleFile(f)
  }

  const onDragOver = (e) => {
    e.preventDefault()
    setDragActive(true)
  }
  const onDragLeave = () => setDragActive(false)

  // ── step 1 → diagnose ───────────────────────────────────────────────────

  const submitDiagnosis = async () => {
    if (mode === 'file' && !file) {
      toast.error('Sube un archivo o pega el texto')
      return
    }
    if (mode === 'paste') {
      const len = pastedText.trim().length
      if (len < MIN_PASTE_CHARS) {
        toast.error(`El borrador es demasiado corto (mínimo ${MIN_PASTE_CHARS} caracteres)`)
        return
      }
      if (len > MAX_PASTE_CHARS) {
        toast.error(`El borrador es demasiado largo (máximo ${MAX_PASTE_CHARS} caracteres)`)
        return
      }
    }

    setStep('diagnosing')
    try {
      const fd = new FormData()
      if (mode === 'file') fd.append('file', file)
      else fd.append('text', pastedText)
      fd.append('platform', platform)
      fd.append('duration_min', String(durationMin))

      const res = await api.post('/scripts/diagnose-draft', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      setDiagnosisResult(res.data)
      setStep('review')
    } catch (err) {
      const msg = err.response?.data?.detail || 'No se pudo diagnosticar el borrador'
      toast.error(typeof msg === 'string' ? msg : 'Error inesperado')
      setStep('upload')
    }
  }

  // ── step 2 → import (resurrect) ─────────────────────────────────────────

  const submitImport = async () => {
    if (!diagnosisResult?.diagnosis_id) return
    setStep('resurrecting')
    try {
      const res = await api.post('/scripts/import-draft', {
        diagnosis_id: diagnosisResult.diagnosis_id,
        platform,
        duration_min: durationMin,
        version_name: 'v1 - Resucitado',
      })
      toast.success('Borrador resucitado. Abriendo editor…')
      onImported?.(res.data)
      onClose()
      navigate('/scripts', {
        state: {
          projectId: res.data.project_id,
          resurrectedScriptId: res.data.script_id,
        },
      })
    } catch (err) {
      const detail = err.response?.data?.detail
      const msg =
        typeof detail === 'string'
          ? detail
          : detail?.message || 'No se pudo resucitar el borrador'
      toast.error(msg)
      setStep('review')
    }
  }

  // ── render ──────────────────────────────────────────────────────────────

  const isWorking = step === 'diagnosing' || step === 'resurrecting'

  return (
    <Dialog open={open} onOpenChange={isWorking ? undefined : onClose}>
      <DialogContent
        className="bg-ds-surface border border-ds-border text-ds-text max-w-2xl max-h-[92vh] overflow-y-auto"
        style={{ borderRadius: 2 }}
        data-testid="draft-import-modal"
      >
        <DialogHeader>
          <DialogTitle className="type-h3 text-ds-text text-base flex items-center gap-2">
            <Sparkle size={18} weight="fill" className="text-ds-red" />
            Resucitar borrador abandonado
          </DialogTitle>
        </DialogHeader>

        {step === 'upload' && (
          <UploadStep
            mode={mode}
            setMode={setMode}
            file={file}
            setFile={setFile}
            pastedText={pastedText}
            setPastedText={setPastedText}
            platform={platform}
            setPlatform={setPlatform}
            durationMin={durationMin}
            setDurationMin={setDurationMin}
            dragActive={dragActive}
            onDrop={onDrop}
            onDragOver={onDragOver}
            onDragLeave={onDragLeave}
            onPick={() => inputRef.current?.click()}
            inputRef={inputRef}
            handleFile={handleFile}
            onCancel={onClose}
            onSubmit={submitDiagnosis}
          />
        )}

        {step === 'diagnosing' && <ProgressStep label="Diagnosticando tu borrador…" />}
        {step === 'resurrecting' && (
          <ProgressStep label="Resucitándolo en tu voz… esto tarda 30-60 segundos." />
        )}

        {step === 'review' && diagnosisResult && (
          <ReviewStep
            result={diagnosisResult}
            onBack={() => setStep('upload')}
            onConfirm={submitImport}
          />
        )}
      </DialogContent>
    </Dialog>
  )
}

// ─── sub-components ──────────────────────────────────────────────────────

function UploadStep({
  mode,
  setMode,
  file,
  setFile,
  pastedText,
  setPastedText,
  platform,
  setPlatform,
  durationMin,
  setDurationMin,
  dragActive,
  onDrop,
  onDragOver,
  onDragLeave,
  onPick,
  inputRef,
  handleFile,
  onCancel,
  onSubmit,
}) {
  return (
    <div className="space-y-4 mt-2">
      <p className="text-ds-muted text-sm leading-relaxed">
        ¿Tienes un guion abandonado de hace semanas? No lo tires.{' '}
        <span className="text-ds-text">Termínalo en tu voz.</span>
      </p>

      {/* mode toggle */}
      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => setMode('file')}
          className={`flex-1 px-3 py-2 text-xs uppercase tracking-wide font-display font-semibold transition-colors ${
            mode === 'file'
              ? 'bg-[rgba(212,43,30,0.12)] text-ds-red border border-[rgba(212,43,30,0.3)]'
              : 'bg-ds-elevated text-ds-muted border border-ds-border hover:text-ds-text'
          }`}
          style={{ borderRadius: 2 }}
        >
          Archivo
        </button>
        <button
          type="button"
          onClick={() => setMode('paste')}
          className={`flex-1 px-3 py-2 text-xs uppercase tracking-wide font-display font-semibold transition-colors ${
            mode === 'paste'
              ? 'bg-[rgba(212,43,30,0.12)] text-ds-red border border-[rgba(212,43,30,0.3)]'
              : 'bg-ds-elevated text-ds-muted border border-ds-border hover:text-ds-text'
          }`}
          style={{ borderRadius: 2 }}
        >
          Pegar texto
        </button>
      </div>

      {/* upload zone */}
      {mode === 'file' && (
        <div
          onDrop={onDrop}
          onDragOver={onDragOver}
          onDragLeave={onDragLeave}
          className={`border-2 border-dashed px-6 py-10 text-center transition-colors cursor-pointer ${
            dragActive
              ? 'border-ds-red bg-[rgba(212,43,30,0.05)]'
              : 'border-ds-border bg-ds-elevated hover:border-ds-border-mid'
          }`}
          style={{ borderRadius: 2 }}
          onClick={onPick}
          data-testid="draft-drop-zone"
        >
          <input
            ref={inputRef}
            type="file"
            accept={ACCEPTED_EXT.join(',')}
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0]
              if (f) handleFile(f)
              e.target.value = ''
            }}
          />
          {file ? (
            <div className="flex items-center justify-center gap-3">
              <FileText size={28} className="text-ds-red" weight="duotone" />
              <div className="text-left">
                <p className="text-ds-text text-sm font-medium">{file.name}</p>
                <p className="text-ds-muted text-xs">
                  {(file.size / 1024).toFixed(1)} KB · listo para diagnosticar
                </p>
              </div>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation()
                  setFile(null)
                }}
                className="p-1 text-ds-muted hover:text-ds-red ml-2"
              >
                <X size={16} />
              </button>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-2">
              <Upload size={28} className="text-[#3A3A3A]" weight="duotone" />
              <p className="font-display font-bold text-[12px] tracking-[0.08em] uppercase text-ds-text">
                Arrastra tu borrador
              </p>
              <p className="text-ds-muted text-xs">
                .docx · .md · .txt — hasta 5 MB
              </p>
            </div>
          )}
        </div>
      )}

      {mode === 'paste' && (
        <div>
          <label className="ds-label">Pega el texto del borrador</label>
          <textarea
            value={pastedText}
            onChange={(e) => setPastedText(e.target.value)}
            placeholder="Pega aquí el texto sin formato del borrador que quieres terminar…"
            rows={10}
            className="ds-input resize-y w-full"
            data-testid="draft-paste-textarea"
          />
          <p className="text-ds-muted text-xs mt-1">
            {pastedText.trim().length} / {MAX_PASTE_CHARS} caracteres
          </p>
        </div>
      )}

      {/* target platform + duration */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="ds-label">Plataforma destino</label>
          <select
            value={platform}
            onChange={(e) => setPlatform(e.target.value)}
            className="ds-select"
          >
            {PLATFORMS.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="ds-label">Duración objetivo (min)</label>
          <input
            type="number"
            min={1}
            max={60}
            value={durationMin}
            onChange={(e) => setDurationMin(parseInt(e.target.value, 10) || 1)}
            className="ds-input"
          />
        </div>
      </div>

      <p className="text-ds-muted text-[11px] leading-relaxed">
        El diagnóstico es <span className="text-ds-text">gratis</span> (máx 5/día). Solo se cobran
        créditos si confirmas la reescritura.
      </p>

      <div className="flex gap-3 pt-2">
        <button onClick={onCancel} className="btn-ghost flex-1 py-2">
          Cancelar
        </button>
        <button
          onClick={onSubmit}
          className="btn-primary flex-1 py-2 flex items-center justify-center gap-2"
          data-testid="draft-diagnose-submit"
        >
          Diagnosticar borrador <ArrowRight size={14} weight="bold" />
        </button>
      </div>
    </div>
  )
}

function ProgressStep({ label }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 px-6 text-center">
      <div className="w-8 h-8 border-2 border-ds-red border-t-transparent rounded-full animate-spin mb-4" />
      <p className="text-ds-text text-sm font-medium">{label}</p>
      <p className="text-ds-muted text-xs mt-1">No cierres esta ventana.</p>
    </div>
  )
}

function ReviewStep({ result, onBack, onConfirm }) {
  const { diagnosis, credit_cost, is_welcome_gift: isWelcomeGift } = result
  const score = Math.round((diagnosis.tone_match_score || 0) * 100)

  return (
    <div className="space-y-4 mt-2">
      <div className="bg-ds-elevated border-l-[3px] border-ds-red px-4 py-3" style={{ borderRadius: 2 }}>
        <p className="text-ds-text text-sm leading-relaxed">{diagnosis.summary}</p>
        <p className="text-ds-muted text-xs mt-2">
          Coincidencia con tu voz: <span className="text-ds-text">{score}%</span>
          {isWelcomeGift && (
            <> · Este va por cuenta de la casa: es tu primer guion.</>
          )}
        </p>
      </div>

      <DiagnosisCard
        icon={<CheckCircle size={16} weight="fill" className="text-emerald-500" />}
        title="Lo que ya funciona"
        emptyText="No hemos encontrado partes claramente fuertes — la reescritura tendrá libertad."
        items={(diagnosis.strong_parts || []).map((p) => ({
          quote: p.quote,
          note: p.reason,
        }))}
      />

      <DiagnosisCard
        icon={<Warning size={16} weight="fill" className="text-amber-500" />}
        title="Lo que está flojo"
        emptyText="Nada marcado como flojo."
        items={(diagnosis.weak_parts || []).map((p) => ({
          quote: p.quote,
          note: p.issue,
        }))}
      />

      <DiagnosisCard
        icon={<Plus size={16} weight="bold" className="text-sky-400" />}
        title="Lo que falta"
        emptyText="No falta nada estructural."
        items={(diagnosis.missing_parts || []).map((m) => ({ quote: m, note: '' }))}
      />

      <div className="flex gap-3 pt-2">
        <button onClick={onBack} className="btn-ghost flex-1 py-2">
          Volver
        </button>
        <button
          onClick={onConfirm}
          className="btn-primary flex-1 py-2 flex items-center justify-center gap-2"
          data-testid="draft-import-confirm"
        >
          Resolver y crear guion ·{' '}
          {isWelcomeGift
            ? 'gratis'
            : `${credit_cost} ${credit_cost === 1 ? 'crédito' : 'créditos'}`}
        </button>
      </div>
    </div>
  )
}

function DiagnosisCard({ icon, title, items, emptyText }) {
  return (
    <div
      className="bg-ds-bg border border-ds-border px-4 py-3"
      style={{ borderRadius: 2 }}
    >
      <div className="flex items-center gap-2 mb-2">
        {icon}
        <h4 className="font-display font-bold text-[11px] tracking-[0.08em] uppercase text-ds-text">
          {title}
        </h4>
        <span className="text-ds-muted text-[10px] ml-auto">
          {items.length} {items.length === 1 ? 'punto' : 'puntos'}
        </span>
      </div>
      {items.length === 0 ? (
        <p className="text-ds-muted text-xs italic">{emptyText}</p>
      ) : (
        <ul className="space-y-2">
          {items.map((it, i) => (
            <li key={i} className="text-xs">
              <p className="text-ds-text leading-relaxed">
                {it.quote.length > 220 ? `${it.quote.slice(0, 220)}…` : it.quote}
              </p>
              {it.note && <p className="text-ds-muted text-[11px] mt-1">{it.note}</p>}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
